import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Grid,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { GuestPass, GuestPassStatus } from '../../types/guestPass';
import { formatDate } from '../../utils/dateUtils';
import { guestPassAPI } from '../../services/api';

interface GuestPassCardProps {
    guestPass: GuestPass;
    onCopyCode?: (code: string) => void;
}

export const GuestPassCard: React.FC<GuestPassCardProps> = ({ guestPass, onCopyCode }) => {
    const [qrCodeBlobUrl, setQrCodeBlobUrl] = useState<string | null>(null);
    const [loadingQRCode, setLoadingQRCode] = useState(false);

    // Fetch QR code as blob
    useEffect(() => {
        const fetchQRCode = async () => {
            if (guestPass?.id) {
                setLoadingQRCode(true);
                try {
                    const response = await guestPassAPI.getQRCode(guestPass.id);
                    const blob = new Blob([response.data], { type: 'image/png' });
                    const blobUrl = URL.createObjectURL(blob);
                    setQrCodeBlobUrl(blobUrl);
                } catch (err) {
                    console.error('Error fetching QR code:', err);
                } finally {
                    setLoadingQRCode(false);
                }
            }
        };

        fetchQRCode();

        // Cleanup function to revoke blob URL
        return () => {
            if (qrCodeBlobUrl) {
                URL.revokeObjectURL(qrCodeBlobUrl);
            }
        };
    }, [guestPass?.id]);

    const getStatusColor = (status: GuestPassStatus) => {
        switch (status) {
            case GuestPassStatus.ACTIVE:
                return 'success';
            case GuestPassStatus.REDEEMED:
                return 'info';
            case GuestPassStatus.EXPIRED:
                return 'warning';
            case GuestPassStatus.CANCELLED:
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: GuestPassStatus) => {
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const handleCopyCode = () => {
        navigator.clipboard.writeText(guestPass.code);
        if (onCopyCode) {
            onCopyCode(guestPass.code);
        }
    };

    const isExpired = new Date(guestPass.validUntil) < new Date() && guestPass.status === GuestPassStatus.ACTIVE;

    return (
        <Card>
            <CardContent>
                <Grid container spacing={2}>
                    {/* QR Code Section */}
                    <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: '#fff',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                                mb: 1
                            }}
                        >
                            {loadingQRCode ? (
                                <Box
                                    sx={{
                                        width: 150,
                                        height: 150,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1
                                    }}
                                >
                                    <CircularProgress size={40} />
                                </Box>
                            ) : qrCodeBlobUrl ? (
                                <img
                                    src={qrCodeBlobUrl}
                                    alt="Guest Pass QR Code"
                                    style={{
                                        width: '150px',
                                        height: '150px',
                                        display: 'block'
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: 150,
                                        height: 150,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" textAlign="center">
                                        QR Code<br />{guestPass.code}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" fontFamily="monospace">
                                {guestPass.code}
                            </Typography>
                            <Tooltip title="Copy code">
                                <IconButton size="small" onClick={handleCopyCode}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Chip 
                            label={getStatusLabel(guestPass.status)} 
                            color={getStatusColor(guestPass.status)} 
                            size="small"
                            sx={{ mt: 1 }}
                        />
                    </Grid>

                    {/* Details Section */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {guestPass.guestName}
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Email: {guestPass.guestEmail}
                                </Typography>
                                {guestPass.guestPhone && (
                                    <Typography variant="body2" color="text.secondary">
                                        Phone: {guestPass.guestPhone}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Issued:</strong> {formatDate(guestPass.issuedAt)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Valid Until:</strong> {formatDate(guestPass.validUntil)}
                                    {isExpired && (
                                        <Chip 
                                            label="Expired" 
                                            color="warning" 
                                            size="small" 
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Typography>
                                {guestPass.redeemedAt && (
                                    <Typography variant="body2">
                                        <strong>Redeemed:</strong> {formatDate(guestPass.redeemedAt)}
                                    </Typography>
                                )}
                            </Box>

                            {guestPass.notes && (
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Notes:</strong> {guestPass.notes}
                                    </Typography>
                                </Box>
                            )}

                            {guestPass.memberProfile && (
                                <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Sponsored by: {guestPass.memberProfile.firstName} {guestPass.memberProfile.lastName}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};
